"use client";

import { useState } from "react";
import Image, { StaticImageData } from "next/image";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "@/assets/icons/mdi-chevron-down";

export type PolicySection = {
  mainText?: string;
  bulletPoints?: string[];
  additionalText?: string[];
  contactInfo?: string;
};

export type PolicyArticle = {
  title: string;
  content: PolicySection[];
};

type PolicyPageProps = {
  heroLabel: string;
  heroTitle: string;
  heroImage: StaticImageData;
  items: PolicyArticle[];
  initialOpenAll?: boolean;
};

export const PolicyPage = ({
  heroLabel,
  heroTitle,
  heroImage,
  items,
  initialOpenAll = true,
}: PolicyPageProps): JSX.Element => {
  const [openArticles, setOpenArticles] = useState<number[]>(
    initialOpenAll ? Array.from({ length: items.length }, (_, i) => i) : [0],
  );

  const toggleArticle = (index: number) => {
    setOpenArticles((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const renderContent = (content: PolicySection[]) => {
    return (
      <div className="flex flex-col gap-5 text-sm leading-relaxed text-black/85 md:text-base">
        {content.map((section, idx) => (
          <div
            key={idx}
            className="rounded-2xl border border-black/10 bg-black/[0.03] p-4 md:p-5"
          >
            {section.mainText && <p>{section.mainText}</p>}

            {section.bulletPoints && (
              <ul className="mt-3 flex flex-col gap-2">
                {section.bulletPoints.map((point, i) => (
                  <li key={i} className="flex items-start">
                    <span className="me-2 text-primaryLoc">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            )}

            {section.additionalText?.map((text, i) => (
              <p key={i}>{text}</p>
            ))}

            {section.contactInfo && (
              <p className="font-semibold text-primaryLoc">
                {section.contactInfo}
              </p>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <main className="bg-black text-white">
      <section className="relative isolate flex min-h-[56vh] items-center justify-center overflow-hidden bg-white">
        <Image
          src={heroImage}
          alt={`KS Rent Services — ${heroTitle}`}
          fill
          priority
          className="object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/70 to-white/90" />
        <motion.div
          className="absolute -left-20 top-10 h-56 w-56 rounded-full bg-primaryLoc/25 blur-3xl"
          animate={{ y: [0, -16, 0], x: [0, 8, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -right-24 bottom-10 h-64 w-64 rounded-full bg-black/15 blur-3xl"
          animate={{ y: [0, 18, 0], x: [0, -10, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="relative z-10 mx-auto max-w-[90rem] px-6 py-24 text-center md:px-8 lg:py-28">
          <motion.p
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-2 text-sm uppercase tracking-[0.35em] text-black/70"
          >
            {heroLabel}
          </motion.p>
          <motion.h1
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-space-age text-3xl tracking-[0.2em] text-black md:text-4xl"
          >
            {heroTitle}
          </motion.h1>
        </div>
      </section>

      <section className="w-full bg-gradient-to-b from-white via-white to-white py-16 text-black">
        <div className="mx-auto max-w-[90rem] px-6 md:px-8">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.45 }}
            className="mb-8 flex flex-wrap items-center gap-3"
          >
            <span className="rounded-full border border-primaryLoc/50 bg-primaryLoc/15 px-3 py-1 text-xs uppercase tracking-[0.16em] text-primaryLoc">
              KS Rent Services
            </span>
            <span className="rounded-full border border-black/20 bg-black/5 px-3 py-1 text-xs uppercase tracking-[0.16em] text-black/70">
              Cadre juridique
            </span>
            <span className="rounded-full border border-black/20 bg-black/5 px-3 py-1 text-xs uppercase tracking-[0.16em] text-black/70">
              Mise à jour continue
            </span>
          </motion.div>
          <div className="space-y-6">
            {items.map((article, index) => (
              <motion.article
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.45, delay: index * 0.03 }}
                whileHover={{
                  y: -4,
                  rotateX: 1.5,
                  rotateY: -1.5,
                  transition: { duration: 0.2 },
                }}
                className="rounded-3xl border border-black/15 bg-black/[0.04] px-4 pb-4 pt-4 shadow-[0_15px_60px_-35px_rgba(0,0,0,0.35)] backdrop-blur-sm md:px-6"
                style={{ transformStyle: "preserve-3d" }}
              >
                <button
                  onClick={() => toggleArticle(index)}
                  className="flex w-full items-center justify-between text-start"
                >
                  <h2 className="text-lg font-semibold text-black md:text-xl">
                    {article.title}
                  </h2>
                  <motion.span
                    animate={{ rotate: openArticles.includes(index) ? 0 : -90 }}
                    transition={{ duration: 0.25 }}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-black/20 bg-black/5"
                  >
                    <ChevronDown width={24} />
                  </motion.span>
                </button>

                <AnimatePresence initial={false}>
                  {openArticles.includes(index) ? (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden pt-5"
                    >
                      {renderContent(article.content)}
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default PolicyPage;
